import React, { useCallback, useEffect, useState } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';

/**
 * Drop-in replacement for <Input type="number" /> that solves three recurring bugs:
 *
 *   1. Keystroke-clamp snap: clamping to [min, max] on every change ate mid-typing
 *      values (e.g. typing "50" would snap "5" to min then "200" to max). We commit
 *      only on blur/Enter and maintain a local text buffer for mid-typing state.
 *
 *   2. NaN in state: `parseFloat("")` returns NaN which React renders as the string
 *      "NaN" in the next paint. We pass raw string while editing, only parse on commit.
 *
 *   3. Leading-zero confusion: value={x || ''} hides the visible zero when state is
 *      legitimately 0, which breaks "0 fee" and "0 miles" semantics. We distinguish
 *      `null` (empty) from `0` (a real value).
 *
 * Contract:
 *   value      : number | null               null means the field is empty
 *   onChange   : (next: number | null) => void
 *                Called on commit (blur / Enter) with a clamped value, or null if cleared.
 *                NOT called on every keystroke, callers that need live preview should
 *                switch to `onInput`, documented below.
 *   min / max  : optional integer bounds (inclusive). Applied only on commit.
 *   step       : native HTML step (affects arrow-key increment). Defaults to "1".
 *   allowDecimals : set true for price-style inputs. Defaults false (whole numbers).
 *   presets    : optional array of numbers shown as one-tap pills below the input.
 *   onInput    : (rawString: string) => void
 *                Fires on every keystroke with the raw (un-parsed) string. Use only
 *                for live previews; don't persist this value to long-term state.
 */
export const NumberField = React.forwardRef(function NumberField(
  {
    value,
    onChange,
    min,
    max,
    step = '1',
    allowDecimals = false,
    presets,
    className,
    testId,
    onInput,
    onBlur,
    placeholder,
    disabled,
    ...rest
  },
  ref,
) {
  const displayValue = value === null || value === undefined ? '' : String(value);
  const [draft, setDraft] = useState(displayValue);

  // Keep the local buffer in sync when parent changes value externally
  // (e.g. form reset on Save Draft resume, preset click, programmatic fill).
  useEffect(() => {
    setDraft(displayValue);
    // Intentionally depend on `displayValue` (derived from `value`) so the
    // buffer follows parent commits but NOT mid-typing value echoes.
  }, [displayValue]);

  const parse = useCallback(
    (raw) => {
      if (raw === '' || raw == null) return null;
      const n = allowDecimals ? parseFloat(raw) : parseInt(raw, 10);
      return Number.isFinite(n) ? n : null;
    },
    [allowDecimals],
  );

  const commit = useCallback(() => {
    const parsed = parse(draft);
    if (parsed === null) {
      onChange(null);
      setDraft('');
      return;
    }
    let clamped = parsed;
    if (typeof min === 'number' && clamped < min) clamped = min;
    if (typeof max === 'number' && clamped > max) clamped = max;
    onChange(clamped);
    setDraft(String(clamped));
  }, [draft, min, max, onChange, parse]);

  return (
    <div className="space-y-2">
      <Input
        ref={ref}
        type="number"
        inputMode={allowDecimals ? 'decimal' : 'numeric'}
        step={step}
        min={min}
        max={max}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(className)}
        data-testid={testId}
        onChange={(e) => {
          setDraft(e.target.value);
          if (onInput) onInput(e.target.value);
        }}
        onBlur={(e) => {
          commit();
          if (onBlur) onBlur(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        {...rest}
      />

      {Array.isArray(presets) && presets.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={testId ? `${testId}-presets` : undefined}
        >
          {presets.map((preset) => {
            const active = value === preset;
            return (
              <button
                key={preset}
                type="button"
                onClick={() => {
                  onChange(preset);
                  setDraft(String(preset));
                }}
                className={cn(
                  'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                  active
                    ? 'bg-[#D4AF37] border-[#D4AF37] text-white'
                    : 'bg-white border-[#E6E4DD] text-stone-600 hover:border-[#D4AF37] hover:text-[#1A1A1A]',
                )}
                data-testid={testId ? `${testId}-preset-${preset}` : undefined}
              >
                {preset}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
